import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	Typography,
	Paper,
	IconButton,
	LinearProgress,
	Divider,
} from "@mui/material";
import {
	Close as CloseIcon,
	ArrowForward as ArrowForwardIcon,
	ArrowBack as ArrowBackIcon,
	CheckCircle as CheckCircleIcon,
	Info as InfoIcon,
	Security as SecurityIcon,
	Speed as SpeedIcon,
	Assessment as AssessmentIcon,
} from "@mui/icons-material";
import "./InteractiveTour.css";

const InteractiveTour = ({ isOpen, onClose }) => {
	const [currentStep, setCurrentStep] = useState(0); //hello
	// Bounding rect of the currently highlighted element (null = no target /
	// centered step). Drives the animated spotlight overlay.
	const [spotlightRect, setSpotlightRect] = useState(null);
	const [modalPosition, setModalPosition] = useState("center");

	// Simplified tour steps
	const tourSteps = [
		{
			id: "overview",
			title: "MAN Parts Tracking Tool",
			subtitle: "Enterprise Parts Development Management",
			content: [
				"Welcome to the MAN Parts Tracking Tool - your centralized platform for managing the complete parts development lifecycle.",
				"This system provides comprehensive oversight from initial supply allocation through production series.",
			],
			targetElement: null,
			modalPosition: "center",
			features: [
				{
					icon: <SecurityIcon />,
					title: "Enterprise Security",
					description: "Secure access control",
				},
				{
					icon: <SpeedIcon />,
					title: "Real-time Tracking",
					description: "Live status updates",
				},
				{
					icon: <AssessmentIcon />,
					title: "Analytics",
					description: "Comprehensive insights",
				},
			],
		},
		{
			id: "workflow",
			title: "Development Workflow",
			subtitle: "Three-Stage Process Overview",
			content: [
				"The parts development process follows a structured three-stage workflow designed to ensure quality and efficiency.",
				"Each stage has defined entry and exit criteria with clear handoff processes between teams.",
			],
			targetElement: ".workflow-diagram",
			modalPosition: "bottom",
			stages: [
				{
					number: "01",
					title: "Supply Type Allocation",
					description: "Procurement planning",
				},
				{
					number: "02",
					title: "Vehicle Tracking",
					description: "Pre-series validation",
				},
				{
					number: "03",
					title: "Series Tracking",
					description: "Production monitoring",
				},
			],
		},
		{
			id: "supply-allocation",
			title: "Supply Type Allocation",
			subtitle: "Foundation of Parts Development",
			content: [
				"The Supply Type Allocation module serves as the entry point for all parts development activities.",
				"Focus on strategic procurement decisions, supplier evaluation, and resource planning.",
			],
			targetElement: '[data-stage="supply-type"]',
			modalPosition: "top-right",
			capabilities: [
				"Procurement strategy definition",
				"Supplier evaluation and selection",
				"Resource allocation planning",
				"Timeline management",
				"Risk assessment",
			],
		},
		{
			id: "vff-process",
			title: "Vehicle Tracking Process",
			subtitle: "Pre-Series Validation & Testing",
			content: [
				"Vehicle Tracking manages the critical pre-series phase where components undergo rigorous testing.",
				"Ensures all parts meet quality standards before entering series production.",
			],
			targetElement: '[data-stage="vff"]',
			modalPosition: "top-right",
			capabilities: [
				"Function validation testing",
				"Quality assurance protocols",
				"Supplier performance monitoring",
				"Compliance verification",
				"Issue tracking and resolution",
			],
		},
		{
			id: "series-production",
			title: "Series Production Tracking",
			subtitle: "Manufacturing Excellence",
			content: [
				"Series Tracking provides comprehensive oversight of the production phase.",
				"Monitor manufacturing progress, quality metrics, and delivery performance.",
			],
			targetElement: '[data-stage="series"]',
			modalPosition: "top-left",
			capabilities: [
				"Production progress monitoring",
				"Quality metrics tracking",
				"Delivery schedule management",
				"Capacity planning",
				"Performance optimization",
			],
			status: "Coming Soon",
		},
		{
			id: "analytics",
			title: "Analytics & Reporting",
			subtitle: "Data-Driven Decision Making",
			content: [
				"The Analytics Dashboard transforms data into actionable insights.",
				"Advanced analytics help identify trends and optimize processes.",
			],
			targetElement: '[data-stage="dashboard"]',
			modalPosition: "center",
			capabilities: [
				"Real-time KPI monitoring",
				"Trend analysis and forecasting",
				"Custom report generation",
				"Performance benchmarking",
				"Executive dashboards",
			],
		},
		{
			id: "navigation",
			title: "Platform Navigation",
			subtitle: "Efficient Workflow Management",
			content: [
				"The platform is designed for intuitive navigation with multiple interaction modes.",
				"Each module can be accessed directly or opened in a new tab for parallel processing.",
			],
			modalPosition: "centre",
			navigationTips: [
				"Click module cards to navigate directly",
				'Use "Open in New Tab" for parallel work',
				"Modules show availability status",
				"Header provides quick access to functions",
			],
		},
		{
			id: "next-steps",
			title: "Getting Started",
			subtitle: "Begin Your Journey",
			content: [
				"You're now ready to leverage the full capabilities of the MAN Parts Tracking Tool.",
				"Start with Supply Type Allocation or explore other areas based on your needs.",
			],
			targetElement: null,
			modalPosition: "center",
			actions: [
				{
					title: "Contact Support",
					description: "Get technical assistance",
					action: "help",
				},
			],
		},
	];

	// Track the target element and keep an animated spotlight aligned to it.
	useEffect(() => {
		const currentTourStep = tourSteps[currentStep];
		setModalPosition(currentTourStep.modalPosition || "center");

		if (!currentTourStep?.targetElement) {
			setSpotlightRect(null);
			return undefined;
		}

		const target = document.querySelector(currentTourStep.targetElement);
		if (!target) {
			setSpotlightRect(null);
			return undefined;
		}

		let cancelled = false;
		const updateRect = () => {
			if (cancelled) return;
			const r = target.getBoundingClientRect();
			setSpotlightRect({
				top: r.top,
				left: r.left,
				width: r.width,
				height: r.height,
			});
		};

		// Bring the target into view, then align the spotlight (immediately and
		// again once the smooth scroll settles).
		target.scrollIntoView({
			behavior: "smooth",
			block: "center",
			inline: "center",
		});
		updateRect();
		const settleTimer = setTimeout(updateRect, 400);

		// Keep the spotlight aligned while the step is active.
		window.addEventListener("scroll", updateRect, true);
		window.addEventListener("resize", updateRect);

		return () => {
			cancelled = true;
			clearTimeout(settleTimer);
			window.removeEventListener("scroll", updateRect, true);
			window.removeEventListener("resize", updateRect);
		};
	}, [currentStep]);

	const handleNext = () => {
		if (currentStep < tourSteps.length - 1) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handlePrevious = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};
	const handleClose = () => {
		// When user clicks the close (X) button, treat it as skipping the tour
		setCurrentStep(0);
		setSpotlightRect(null);
		onClose();
	};

	const handleSkipTour = () => {
		// When user clicks "Skip Tour" button, skip the entire tour
		setCurrentStep(0);
		setSpotlightRect(null);
		onClose();
	};
	const handleActionClick = (action) => {
		if (action.action === "help") {
			// Open help/support via email (same as the "Need Help" button in the header)
			window.location.href =
				"mailto:yash.sakhare@man.eu?subject=Need%20Help%20with%20MAN%20Parts%20Tracking%20Tool";
		}
	};

	const currentTourStep = tourSteps[currentStep];
	const progress = ((currentStep + 1) / tourSteps.length) * 100;

	if (!isOpen) return null;

	return (
		<Box className="tour-overlay">
			{/* Steps with a target render an animated spotlight cut-out that tracks
			   the element; other steps just dim the page with a plain backdrop. */}
			{spotlightRect ? (
				<div
					className="tour-spotlight"
					style={{
						top: `${spotlightRect.top - 8}px`,
						left: `${spotlightRect.left - 8}px`,
						width: `${spotlightRect.width + 16}px`,
						height: `${spotlightRect.height + 16}px`,
					}}
				/>
			) : (
				<Box className="tour-backdrop" />
			)}{" "}
			<Paper
				className={`tour-modal tour-modal-${modalPosition}`}
				elevation={8}
				sx={{
					maxWidth: "520px",
					width: "485px",
					maxHeight: "75vh",
					overflow: "hidden",
					borderRadius: "12px",
					background: "#ffffff",
					position: "relative",
					zIndex: 10000,
					boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
					opacity: 1,
					transform: "scale(1)",
				}}
			>
				{" "}
				{/* Header */}
				<Box
					sx={{
						background: "linear-gradient(135deg, #1e2832 0%, #2a3a47 100%)",
						color: "white",
						p: 2.2,
					}}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "flex-start",
						}}
					>
						<Box sx={{ flex: 1 }}>
							{" "}
							<Typography
								variant="h6"
								sx={{
									fontWeight: 600,
									fontFamily: "MAN EUROPE_Bold",
									mb: 0.5,
									fontSize: "1.15rem",
								}}
							>
								{currentTourStep.title}
							</Typography>
							<Typography
								variant="body2"
								sx={{
									opacity: 0.9,
									fontSize: "0.95rem",
								}}
							>
								{currentTourStep.subtitle}
							</Typography>
						</Box>{" "}
						<IconButton
							onClick={handleSkipTour}
							size="small"
							sx={{
								color: "white",
								"&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
							}}
							title="Skip Tour"
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>

					<Box sx={{ mt: 2 }}>
						<LinearProgress
							variant="determinate"
							value={progress}
							sx={{
								height: 3,
								borderRadius: 2,
								backgroundColor: "rgba(255,255,255,0.2)",
								"& .MuiLinearProgress-bar": {
									backgroundColor: "#cc0000",
									borderRadius: 2,
								},
							}}
						/>
						<Typography
							variant="caption"
							sx={{
								mt: 0.5,
								display: "block",
								textAlign: "center",
								opacity: 0.8,
								fontSize: "0.65rem",
							}}
						>
							{currentStep + 1} of {tourSteps.length}
						</Typography>
					</Box>
				</Box>
				{/* Content */}{" "}
				<Box
					sx={{
						p: 2.8,
						overflow: "auto",
						maxHeight: "calc(75vh - 150px)",
					}}
				>
					{/* Main Content */}
					<Box sx={{ mb: 2 }}>
						{currentTourStep.content.map((paragraph, index) => (
							<Typography
								key={index}
								variant="body2"
								sx={{
									mb: 1,
									lineHeight: 1.5,
									fontSize: "1rem",
									color: "#444",
									"&:last-child": { mb: 0 },
								}}
							>
								{paragraph}
							</Typography>
						))}
					</Box>

					{/* Features */}
					{currentTourStep.features && (
						<Box sx={{ mb: 2 }}>
							<Typography
								variant="subtitle2"
								sx={{
									mb: 1,
									fontWeight: 600,
									color: "#1e2832",
									fontSize: "0.9rem",
								}}
							>
								Key Features
							</Typography>
							{currentTourStep.features.map((feature, index) => (
								<Box
									key={index}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										mb: 1,
										p: 1,
										borderRadius: "4px",
										backgroundColor: "#f8f9fa",
									}}
								>
									<Box sx={{ color: "#cc0000", fontSize: "1rem" }}>
										{feature.icon}
									</Box>
									<Box>
										<Typography
											variant="body2"
											sx={{
												fontWeight: 600,
												color: "#1e2832",
												fontSize: "0.8rem",
											}}
										>
											{feature.title}
										</Typography>
										<Typography
											variant="body2"
											sx={{
												color: "#666",
												fontSize: "0.7rem",
											}}
										>
											{feature.description}
										</Typography>
									</Box>
								</Box>
							))}
						</Box>
					)}

					{/* Stages */}
					{currentTourStep.stages && (
						<Box sx={{ mb: 2 }}>
							<Typography
								variant="subtitle2"
								sx={{
									mb: 1,
									fontWeight: 600,
									color: "#1e2832",
									fontSize: "0.9rem",
								}}
							>
								Development Stages
							</Typography>
							{currentTourStep.stages.map((stage, index) => (
								<Box
									key={index}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										mb: 1,
										p: 1,
										borderRadius: "4px",
										backgroundColor: "#f8f9fa",
									}}
								>
									<Box
										sx={{
											minWidth: 24,
											height: 24,
											borderRadius: "50%",
											background: "#cc0000",
											color: "white",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontWeight: "bold",
											fontSize: "0.7rem",
										}}
									>
										{stage.number}
									</Box>
									<Box>
										<Typography
											variant="body2"
											sx={{
												fontWeight: 600,
												color: "#1e2832",
												fontSize: "0.8rem",
											}}
										>
											{stage.title}
										</Typography>
										<Typography
											variant="body2"
											sx={{
												color: "#666",
												fontSize: "0.7rem",
											}}
										>
											{stage.description}
										</Typography>
									</Box>
								</Box>
							))}
						</Box>
					)}

					{/* Capabilities */}
					{currentTourStep.capabilities && (
						<Box sx={{ mb: 2 }}>
							<Typography
								variant="subtitle2"
								sx={{
									mb: 1,
									fontWeight: 600,
									color: "#1e2832",
									fontSize: "0.9rem",
								}}
							>
								Key Capabilities
							</Typography>
							<Box sx={{ bgcolor: "#f8f9fa", borderRadius: "4px", p: 1 }}>
								{currentTourStep.capabilities.map((capability, index) => (
									<Box
										key={index}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
											mb: 0.5,
											"&:last-child": { mb: 0 },
										}}
									>
										<CheckCircleIcon
											sx={{ color: "#cc0000", fontSize: "0.8rem" }}
										/>
										<Typography
											variant="body2"
											sx={{
												fontSize: "0.75rem",
												color: "#444",
											}}
										>
											{capability}
										</Typography>
									</Box>
								))}
							</Box>
							{currentTourStep.status && (
								<Box sx={{ mt: 1, textAlign: "center" }}>
									<Typography
										variant="body2"
										sx={{
											color: "#ff9800",
											fontWeight: 600,
											px: 1,
											py: 0.25,
											backgroundColor: "#fff3e0",
											borderRadius: "8px",
											display: "inline-block",
											fontSize: "0.7rem",
										}}
									>
										{currentTourStep.status}
									</Typography>
								</Box>
							)}
						</Box>
					)}

					{/* Navigation Tips */}
					{currentTourStep.navigationTips && (
						<Box sx={{ mb: 2 }}>
							<Typography
								variant="subtitle2"
								sx={{
									mb: 1,
									fontWeight: 600,
									color: "#1e2832",
									fontSize: "0.9rem",
								}}
							>
								Navigation Tips
							</Typography>
							<Box sx={{ bgcolor: "#f8f9fa", borderRadius: "4px", p: 1 }}>
								{currentTourStep.navigationTips.map((tip, index) => (
									<Box
										key={index}
										sx={{
											display: "flex",
											alignItems: "flex-start",
											gap: 0.5,
											mb: 0.5,
											"&:last-child": { mb: 0 },
										}}
									>
										<InfoIcon
											sx={{ color: "#1e2832", fontSize: "0.8rem", mt: 0.125 }}
										/>
										<Typography
											variant="body2"
											sx={{
												fontSize: "0.75rem",
												color: "#444",
												lineHeight: 1.4,
											}}
										>
											{tip}
										</Typography>
									</Box>
								))}
							</Box>
						</Box>
					)}

					{/* Actions */}
					{currentTourStep.actions && (
						<Box sx={{ mb: 2 }}>
							<Typography
								variant="subtitle2"
								sx={{
									mb: 1,
									fontWeight: 600,
									color: "#1e2832",
									fontSize: "0.9rem",
								}}
							>
								What's Next?
							</Typography>{" "}
							{currentTourStep.actions.map((action, index) => (
								<Box
									key={index}
									onClick={() => handleActionClick(action)}
									sx={{
										p: 1,
										mb: 1,
										borderRadius: "4px",
										backgroundColor: "#f8f9fa",
										border: "1px solid #e9ecef",
										cursor: "pointer",
										"&:hover": {
											borderColor: "#cc0000",
											backgroundColor: "#fff",
										},
										"&:last-child": { mb: 0 },
									}}
								>
									<Typography
										variant="body2"
										sx={{
											fontWeight: 600,
											color: "#1e2832",
											fontSize: "0.8rem",
											mb: 0.25,
										}}
									>
										{action.title}
									</Typography>
									<Typography
										variant="body2"
										sx={{
											color: "#666",
											fontSize: "0.7rem",
										}}
									>
										{action.description}
									</Typography>
								</Box>
							))}
						</Box>
					)}
				</Box>
				{/* Footer Controls */}
				<Divider />
				<Box
					sx={{
						p: 1.5,
						backgroundColor: "#f8f9fa",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Box sx={{ display: "flex", gap: 1 }}>
						<Button
							onClick={handlePrevious}
							disabled={currentStep === 0}
							variant="outlined"
							size="small"
							startIcon={<ArrowBackIcon />}
							sx={{
								borderColor: "#1e2832",
								color: "#1e2832",
								fontSize: "0.75rem",
								"&:hover": { borderColor: "#cc0000", color: "#cc0000" },
							}}
						>
							Prev
						</Button>

						<Button
							onClick={handleSkipTour}
							variant="text"
							size="small"
							sx={{
								color: "#666",
								fontSize: "0.75rem",
								"&:hover": { color: "#cc0000" },
							}}
						>
							Skip Tour
						</Button>
					</Box>

					<Typography
						variant="body2"
						sx={{ color: "#666", fontSize: "0.7rem" }}
					>
						{currentStep + 1} / {tourSteps.length}
					</Typography>

					{currentStep < tourSteps.length - 1 ? (
						<Button
							onClick={handleNext}
							variant="contained"
							size="small"
							endIcon={<ArrowForwardIcon />}
							sx={{
								bgcolor: "#1e2832",
								fontSize: "0.75rem",
								"&:hover": { bgcolor: "#cc0000" },
							}}
						>
							Next
						</Button>
					) : (
						<Button
							onClick={handleClose}
							variant="contained"
							size="small"
							sx={{
								bgcolor: "#cc0000",
								fontSize: "0.75rem",
								"&:hover": { bgcolor: "#b30000" },
							}}
						>
							Start
						</Button>
					)}
				</Box>
			</Paper>
		</Box>
	);
};

export default InteractiveTour;
